import { useEffect, useState } from "react";
import { Outlet, useNavigate, Navigate } from "react-router-dom";
import { styled, Container, Box, useTheme, Typography } from "@mui/material";
import { SidebarV3 as Sidebar } from "gf-ui-components-mui-lib";
import { useSelector, AppState, dispatch } from "@/redux/store";
import { handlingRoles } from "@/redux/apps/users/UserSlice";
import HeaderLayout from "@/layouts/landing/header/Header";
import { setCookie } from "@/utils";
import { getProductConfigForSubdomain, productListMapping } from "@/utils/helpers";
import { applicationTitle, getSubdomain } from "@/utils/common";
import { COMMON_SUB_DOMAIN } from "@/config";
import { Helmet } from "react-helmet-async";
import GroupSVG from "@/images/svg/group.svg";

const MainWrapper = styled("div")(() => ({
  display: "flex",
  minHeight: "100vh",
  width: "100%",
  //background: 'rgba(255, 255, 255, 0.30)',
  backgroundColor: "#e6e8ec",
  overflow: "auto"
}));

const PageWrapper = styled("div")(() => ({
  display: "flex",
  flexGrow: 1,
  paddingBottom: "43px",
  flexDirection: "column",
  zIndex: 1,
  width: "100%",
  backgroundColor: "transparent"
}));

const CreateLayout = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const title = applicationTitle();
  const subDomain = getSubdomain();

  const customizer = useSelector((state: AppState) => state.customizer);
  const { loggedUserOrganisations } = useSelector((state) => state.user);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [isOrgFetched, setIsOrgFetched] = useState<boolean>(false);
  const [showProductDropdown, setShowProductDropdown] = useState<boolean>(false);
  const [listOfProducts, setListOfProducts] = useState<any>([]);

  useEffect(() => {
    const products = productListMapping(loggedUserOrganisations);
    setListOfProducts(products);
  }, [loggedUserOrganisations]);

  useEffect(() => {
    if (
      loggedUserOrganisations &&
      loggedUserOrganisations?.length === 1 &&
      !isOrgFetched &&
      !selectedItem
    ) {
      handleOrganizationSelect(loggedUserOrganisations[0]);
      setIsOrgFetched(true);
    }
  }, [loggedUserOrganisations]);

  const handleOrganizationSelect = async (selected: any) => {
    if (selected) {
      await dispatch(
        handlingRoles({
          roles: loggedUserOrganisations
        })
      );
      setSelectedItem(selected);
      setCookie("xsm_user_organization", JSON.stringify(selected));
      navigate("/dashboard");
    }
  };

  const changeTheProducts = (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setShowProductDropdown(!showProductDropdown);
  };

  const handleProductSelect = (item: any) => {
    window.location.href = item?.link;
  };

  const isAuthenticatedLandingLayout = () => {
    const subDomain = getSubdomain();
    const defaultSubDomain =
      subDomain !== "" && subDomain !== COMMON_SUB_DOMAIN ? subDomain : "xsm";
    const accessTokenKey = `${defaultSubDomain}_access_token`;
    // Check for subdomain-specific token first, then fallback to generic token
    const accessToken =
      localStorage.getItem(accessTokenKey) || localStorage.getItem("xsm_access_token");
    return !!accessToken;
  };

  const productConfig = getProductConfigForSubdomain(subDomain);
  const Icon = productConfig?.icon;
  const productDisplayName = productConfig?.displayName;

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <MainWrapper>
        {isAuthenticatedLandingLayout() ? (
          <>
            <Sidebar
              variant={"permanent"}
              anchor={"left"}
              sidedata={[]}
              sx={{
                ".MuiDrawer-paper": {
                  borderRight: "none !important"
                }
              }}
              isDropdownOpen={showProductDropdown}
              dropdownItems={listOfProducts}
              shimmerEffect={true}
              disabledLink={true}
              handleDropdownSelect={handleProductSelect}
              changeTheProducts={changeTheProducts}
              selectedItem={selectedItem}
              childrenHead={
                <Typography
                  sx={{
                    color: "white",
                    fontSize: "12px",
                    fontFamily: "Inter",
                    fontWeight: "600",
                    textTransform: "capitalize",
                    wordWrap: "break-word",
                    lineHeight: "normal",
                    mt: "2px"
                  }}
                >
                  Select Organization
                </Typography>
              }
              groupIcon={
                <img src={GroupSVG} alt="logo" height={20} width={20} style={{ margin: "0 8px" }} />
              }
              productIcon={
                <>
                  {Icon && <img
                    src={Icon}
                    alt="product icon"
                    height={35}
                    width={35}
                    style={{ margin: "0 4px" }}
                  />}
                  {productDisplayName && <Typography
                    sx={{ fontSize: "12px", fontWeight: "600", textTransform: "capitalize", margin: "0 10px" }}
                  >
                    {productDisplayName}
                  </Typography>
                  }
                </>
              }
            />
            <PageWrapper
              className="page-wrapper"
              sx={{
                ...(customizer.isCollapse && {
                  [theme.breakpoints.up("lg")]: {
                    ml: `${customizer.MiniSidebarWidth}px`
                  }
                })
              }}
            >
              <HeaderLayout
                isDropdownOpen={true}
                dropdownItems={loggedUserOrganisations}
                handleDropdownSelect={handleOrganizationSelect}
              />
              <Container maxWidth={false} sx={{ padding: 0, margin: 0 }}>
                <Box sx={{ minHeight: "calc(100vh - 170px)", minWidth: "calc(100vw - 281px)" }}>
                  <Outlet />
                </Box>
              </Container>
            </PageWrapper>
          </>
        ) : (
          <Navigate to="/" replace />
        )}
      </MainWrapper>
    </>
  );
};

export default CreateLayout;
